import { useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Btn, Input, Label } from "../components/UI";
import { ShieldCheck, ShieldOff, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Security() {
  const { user, setUser } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");

  const startSetup = async () => {
    const { data } = await api.post("/auth/2fa/setup");
    setSetup(data);
  };

  const enable = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("Two-factor authentication enabled");
      setUser({ ...user, totp_enabled: true });
      setSetup(null); setCode("");
    } catch (err) { toast.error(apiError(err)); }
  };

  const disable = async () => {
    await api.post("/auth/2fa/disable");
    toast.success("Two-factor authentication disabled");
    setUser({ ...user, totp_enabled: false });
  };

  return (
    <div>
      <PageHeader title="Security & 2FA" subtitle="Protect your account with TOTP two-factor authentication" />
      <Card className="p-6 max-w-xl">
        <div className="flex items-center gap-3 mb-4">
          {user?.totp_enabled ? <ShieldCheck size={22} className="text-[#2E7D32]" /> : <ShieldOff size={22} className="text-[#84675B]" />}
          <div>
            <div className="font-heading font-bold text-[#3E2723]">Two-Factor Authentication</div>
            <div className="text-sm text-[#6D4C41]">{user?.totp_enabled ? "Enabled - a code is required at every sign-in." : "Disabled - enable for HIPAA-aligned access control."}</div>
          </div>
        </div>

        {user?.totp_enabled ? (
          <Btn data-testid="disable-2fa-btn" variant="danger" onClick={disable}>Disable 2FA</Btn>
        ) : !setup ? (
          <Btn data-testid="setup-2fa-btn" onClick={startSetup}>Set up 2FA</Btn>
        ) : (
          <form onSubmit={enable} className="space-y-4">
            <div className="rounded-xl bg-[#F5F2EB] p-4">
              <div className="text-xs font-bold uppercase tracking-[0.05em] text-[#7B6157] mb-1">Secret key (add to authenticator app)</div>
              <div className="flex items-center gap-2">
                <code data-testid="totp-secret" className="text-sm font-mono text-[#3E2723] break-all">{setup.secret}</code>
                <button type="button" data-testid="copy-secret-btn" aria-label="Copy secret" onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Copied"); }} className="p-1.5 rounded-full hover:bg-white"><Copy size={14} /></button>
              </div>
              <p className="mt-2 text-xs text-[#84675B]">Open Google Authenticator / Authy → Add account → Enter key manually.</p>
            </div>
            <div>
              <Label>Verification code</Label>
              <Input data-testid="enable-2fa-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6} required />
            </div>
            <div className="flex gap-2">
              <Btn data-testid="enable-2fa-btn" type="submit">Verify & Enable</Btn>
              <Btn type="button" variant="ghost" onClick={() => setSetup(null)}>Cancel</Btn>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
